/**
 * AgentPlanDrawer — generic propose-review-apply drawer for surface-model agents.
 *
 * Connects to the unified /agent/run + /agent/resume endpoints and drives phase
 * transitions from SSE events (plan_ready, apply_complete, tool_progress, etc.).
 *
 * Props:
 *   caseId        — string, required
 *   surfaceId     — string, e.g. "drawer.propagate"
 *   PlanRenderer  — React component rendered in the "ready" phase.
 *                   Receives: { plan, selection, onSelectionChange }
 *   onApplied     — callback() after successful apply
 *   onClose       — callback() to dismiss drawer
 *   labels        — { title, runningMsg, readyMsg, applyBtn, doneMsg }
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import useChatEvents from '../../hooks/useChatEvents';
import ArcOrb from '../ArcOrb';

const PLAN_TIMEOUT_MS = 180_000;
const APPLY_TIMEOUT_MS = 90_000;

const DEFAULT_LABELS = {
  title: 'Agent Plan',
  runningMsg: 'Analyzing…',
  readyMsg: 'Review the proposed changes below.',
  applyBtn: 'Apply changes',
  doneMsg: '✓ Changes applied.',
};

export default function AgentPlanDrawer({
  caseId,
  surfaceId,
  PlanRenderer,
  onApplied,
  onClose,
  labels = {},
}) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const { token } = useAuth();

  const [phase, setPhase] = useState('idle');
  const [conversationId, setConversationId] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [chatter, setChatter] = useState([]);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(new Set());

  const conversationIdRef = useRef(null);
  const phaseRef = useRef(phase);
  const seenSeqsRef = useRef(new Set());
  const watchdogRef = useRef(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const startWatchdog = useCallback((ms, message) => {
    stopWatchdog();
    watchdogRef.current = setTimeout(() => {
      setError(message);
      setPhase('error');
    }, ms);
  }, [stopWatchdog]);

  useEffect(() => () => stopWatchdog(), [stopWatchdog]);

  // ── SSE event handling ───────────────────────────────────────────────
  const handleEvents = useCallback((events) => {
    // Track terminal events seen WITHIN this batch — phaseRef.current
    // doesn't update until the next render, so a plan_ready followed by
    // agent_done in the same poll batch would otherwise trip the
    // "no plan generated" branch even though the plan was delivered.
    let sawPlanReady = false;
    let sawPlanError = false;
    let sawApplyComplete = false;

    for (const evt of events) {
      if (evt.sequence != null) {
        if (seenSeqsRef.current.has(evt.sequence)) continue;
        seenSeqsRef.current.add(evt.sequence);
      }
      const t = evt.event_type || evt.type;
      const d = evt.data ?? evt;

      if (t === 'tool_progress') {
        setChatter(prev => [...prev, {
          kind: d.kind === 'done' ? 'done' : 'started',
          text: d.message || d.tool || '',
        }]);
      } else if (t === 'progress') {
        const text = d.message || d.text || '';
        if (text) setChatter(prev => [...prev, { kind: 'info', text }]);
      } else if (t === 'plan_ready') {
        sawPlanReady = true;
        stopWatchdog();
        setPlanData(d);
        setSelection(new Set(extractAllDocIds(d)));
        setPhase('ready');
      } else if (t === 'apply_complete') {
        sawApplyComplete = true;
        stopWatchdog();
        setPhase('done');
        onApplied?.();
      } else if (t === 'plan_error') {
        sawPlanError = true;
        stopWatchdog();
        setError(d.message || 'Arc encountered an error.');
        setPhase('error');
      } else if (t === 'agent_done') {
        // Only fire the "no plan / no apply" error if the corresponding
        // terminal event wasn't already in this same batch and the phase
        // is still mid-flight from a prior render.
        const p = phaseRef.current;
        if (p === 'running' && !sawPlanReady && !sawPlanError) {
          stopWatchdog();
          setError('Arc responded but did not generate a plan. Please try again.');
          setPhase('error');
        } else if (p === 'applying' && !sawApplyComplete && !sawPlanError) {
          stopWatchdog();
          setError('Arc responded but did not apply the plan. Please try again.');
          setPhase('error');
        }
      }
    }
  }, [onApplied, stopWatchdog]);

  const { start: startEvents, stop: stopEvents, getSeq } = useChatEvents(
    caseId, conversationId,
    { getToken: () => token, onEvents: handleEvents, intervalMs: 750 },
  );

  useEffect(() => () => stopEvents(), [stopEvents]);

  // ── Actions ──────────────────────────────────────────────────────────
  async function handleStart() {
    setPhase('running');
    setError(null);
    setChatter([]);
    setPlanData(null);
    seenSeqsRef.current = new Set();
    try {
      const result = await api.post('/agent/run', {
        surface_id: surfaceId,
        case_id: caseId,
        scope: {},
      });
      conversationIdRef.current = result.conversation_id;
      setConversationId(result.conversation_id);
      startEvents(0, result.conversation_id);
      startWatchdog(PLAN_TIMEOUT_MS, 'Arc took too long to generate a plan. Please try again.');
    } catch (err) {
      setError('Failed to start: ' + err.message);
      setPhase('error');
    }
  }

  async function handleApply() {
    if (!conversationId || !planData) return;
    setPhase('applying');
    setError(null);
    try {
      await api.post('/agent/resume', {
        conversation_id: conversationId,
        case_id: caseId,
        user_input: JSON.stringify({ action: 'apply', selected_doc_ids: [...selection] }),
      });
      startEvents(getSeq(), conversationId);
      startWatchdog(APPLY_TIMEOUT_MS, 'Arc took too long to apply. Please try again.');
    } catch (err) {
      setError('Apply failed: ' + err.message);
      setPhase('ready');
    }
  }

  function handleDiscard() {
    onClose();
  }

  // ── Chatter feed ─────────────────────────────────────────────────────
  function renderChatter() {
    if (!chatter.length) return <p className="ap-hint">{L.runningMsg}</p>;
    return (
      <div className="ap-chatter">
        {chatter.map((ev, i) => (
          <div key={i} className={`ap-chatter-row ap-chatter-${ev.kind || 'info'}`}>
            <span className="ap-chatter-icon">
              {ev.kind === 'done' ? '✓ ' : ev.kind === 'started' ? '› ' : '· '}
            </span>
            {ev.text}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="reorg-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="reorg-drawer ap-drawer">
        {/* Header */}
        <div className="reorg-header">
          <span className="reorg-title">{L.title}</span>
          <button className="btn btn-ghost btn-sm reorg-close" onClick={onClose}>✕</button>
        </div>

        {/* Idle */}
        {phase === 'idle' && (
          <div className="reorg-body reorg-setup">
            <p className="reorg-desc">{L.readyMsg}</p>
            <button className="btn btn-primary reorg-start-btn" onClick={handleStart}>
              Generate plan
            </button>
          </div>
        )}

        {/* Running */}
        {phase === 'running' && (
          <div className="reorg-body reorg-waiting">
            <div className="reorg-waiting-header">
              <ArcOrb size={36} />
              <p>Working…</p>
            </div>
            {renderChatter()}
          </div>
        )}

        {/* Ready: plan review */}
        {phase === 'ready' && planData && (
          <div className="ap-ready-body">
            {renderChatter()}
            {PlanRenderer && (
              <PlanRenderer
                plan={planData}
                selection={selection}
                onSelectionChange={setSelection}
              />
            )}
          </div>
        )}

        {/* Applying */}
        {phase === 'applying' && (
          <div className="reorg-body reorg-waiting">
            <div className="reorg-waiting-header">
              <ArcOrb size={36} />
              <p>Applying changes…</p>
            </div>
            {renderChatter()}
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div className="reorg-body reorg-done">
            <p className="reorg-done-msg">{L.doneMsg}</p>
            {renderChatter()}
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        )}

        {/* Error */}
        {error && <div className="reorg-error">{error}</div>}

        {/* Footer */}
        {phase === 'ready' && (
          <div className="reorg-footer">
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={selection.size === 0}
            >
              {L.applyBtn}
              {selection.size > 0 ? ` (${selection.size})` : ''}
            </button>
            <button className="btn btn-ghost" onClick={handleDiscard}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

function extractAllDocIds(plan) {
  if (!plan || !Array.isArray(plan.corrections)) return [];
  const ids = [];
  for (const corr of plan.corrections) {
    for (const m of (corr.matches || [])) {
      ids.push(m.doc_id);
    }
  }
  return ids;
}
